import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { createUseStyles } from 'react-jss'

import { sortTimeArray, mergeTimeArray, findNextPunchIn, findNextPunchOut, splitShiftByLeaves, getWeekDay } from '../../modules/uitls';
import PunchClock from './PunchClock';
import { tabletMedia } from '../../constants/index';

const useStyles = createUseStyles({
  dateComment: {
    marginBottom: '3px',
    color: '#0000ff',
    backgroundColor: '#efeff4',
    textAlign: 'center',
    // whiteSpace: 'nowrap',
    lineHeight: 1.2,
    padding: '1px',
    fontSize: '14px',
    borderRadius: '2px',
    width: '85px',
    display: 'block',
    justifyContent: 'center',
    alignItems: 'center',
    overflowWrap: 'break-word',
    [tabletMedia]: {
      width: '160px',
      letterSpacing: '5px'
    }
  },
  dateLeave: {
    marginBottom: '3px',
    color: '#efeff4',
    fontWeight: 400,
    lineHeight: 1.2,
    padding: '1px',
    display: 'block',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    width: '85px',
    borderRadius: '.25rem',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    [tabletMedia]: {
      width: '160px',
      letterSpacing: '5px',
      flexDirection: 'row'
    }
  },
  datePunchClock: {
    marginBottom: '3px',
    color: '#fff',
    backgroundColor: '#ffbf38',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
    padding: '1px',
    fontSize: '12px',
    borderRadius: '.25rem',
    width: '85px',
    verticalAlign: 'baseline',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [tabletMedia]: {
      width: '160px',
      letterSpacing: '5px'
    }
  },
  datePunchClockException: {
    marginBottom: '3px',
    color: '#fff',
    backgroundColor: '#17a2b8',
    fontWeight: 400,
    lineHeight: 1.2,
    padding: '1px',
    display: 'block',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    width: '85px',
    borderRadius: '.25rem',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    [tabletMedia]: {
      width: '160px',
      letterSpacing: '5px',
      flexDirection: 'row'
    }
  },
  punchClockError: {
    color: 'red'
  },
  punchClockRevised: {
    color: 'blue'
  }
});

function ScheduleDate({ date, staff, staffId, dataMapping, onSubmit, punchClock, punchClockRevise, workTime, isDayOff, userRight, staffType }) {
  dayjs.extend(isSameOrBefore);
  dayjs.extend(isSameOrAfter);
  const { formatMessage } = useIntl()
  const classes = useStyles();
  const punchClockExceptions = dataMapping.punchClockException ? dataMapping.punchClockException.filter(m => dayjs(m.date).isSame(dayjs(date)) && m.department.includes(staff.department)) : []
  const comments = dataMapping.comment && dataMapping.comment.filter(m => dayjs(m.date).isSame(dayjs(date)) && m.staff === staff.id)
  const weeklyLeaves = dataMapping.weeklyLeave ? dataMapping.weeklyLeave.filter(m => dayjs(m.date).isSame(dayjs(date)) && m.staff === staff.id) : []
  const leaves = dataMapping.leave ? dataMapping.leave.filter(m => m.createdBy === staff.id && (dayjs(date).isSameOrAfter(dayjs(m.startDate)) && dayjs(date).isSameOrBefore(dayjs(m.endDate)))) : []
  const punchClocks = punchClock[dayjs(date).format('YYYY-MM-DD')] ? punchClock[dayjs(date).format('YYYY-MM-DD')][staff.id] : []
  const punchClockRevises = punchClockRevise[dayjs(date).format('YYYY-MM-DD')] ? punchClockRevise[dayjs(date).format('YYYY-MM-DD')][staff.id] : []
  const punchClockArray = punchClockAlgorithm();

  function onEditClick(event, content) {
    if (content === 'punchClock') {
      const punchClockData = {
        date: dayjs(date).format('YYYY-MM-DD'),
        staffId: staffId
      }
      onSubmit(punchClocks, content, punchClockData, punchClockRevises);
    } else {
      if (!userRight['schedule-edit']) {
        return false
      }
      onSubmit(event, content);
    }
  }

  function punchClockAlgorithm() {
    let newPunchClock = new PunchClock(date, punchClocks, punchClockRevises)
    const weekday = dayjs(date).day();
    const exceptionOff = punchClockExceptions.filter(pc => pc.type === 'off');
    const exceptionOn = punchClockExceptions.filter(pc => pc.type === 'on');
    const sortedLeaves = sortTimeArray(leaves.concat(exceptionOff).concat(weeklyLeaves));

    let weekdayShift;

    if (['nurse', 'customerService', 'salesRep'].includes(staffType) && !['santea', 'lexcellence'].includes(process.env.BRANCH_ENV)) {
      if (weekday === 0) {
        weekdayShift = [];
      } else {
        weekdayShift = {
          date: dayjs(date).format('YYYY-MM-DD'),
          startTime: workTime[getWeekDay(dayjs(date))]?.startTime ?? '',
          endTime: workTime[getWeekDay(dayjs(date))]?.endTime ?? ''
        }
      }
    } else {
      if (weekday === 0 || weekday === 6) {
        weekdayShift = [];
      } else {
        weekdayShift = {
          date: dayjs(date).format('YYYY-MM-DD'),
          startTime: workTime[getWeekDay(dayjs(date))]?.startTime ?? '',
          endTime: workTime[getWeekDay(dayjs(date))]?.endTime ?? ''
        }
      }
    }


    const shift = weekdayShift;
    shift.startTimeM = dayjs(date).format('YYYY-MM-DD') + ' ' + shift.startTime
    shift.endTimeM = dayjs(date).format('YYYY-MM-DD') + ' ' + shift.endTime

    const fullShifts = mergeTimeArray(sortTimeArray(exceptionOn.map(e => ({
      date: e.date,
      startTime: e.startTime,
      endTime: e.endTime,
      startTimeM: dayjs(`${e.date} ${e.startTime}`, 'YYYY-MM-DD HH:mm'),
      endTimeM: dayjs(`${e.date} ${e.endTime}`, 'YYYY-MM-DD HH:mm')
    })).concat(shift)));

    let shifts = [];
    for (const shift of fullShifts) {
      shifts = shifts.concat(splitShiftByLeaves(shift, sortedLeaves));
    }

    if (shifts.length === 0 && !newPunchClock.isEmpty) {
      let punchClockDupe = newPunchClock.all.concat();
      const endTime = punchClockDupe[punchClockDupe.length - 1]
      const startTime = punchClockDupe[0]
      shifts.push({
        date: date,
        endTime: endTime,
        startTime: startTime,
        startTimeM: dayjs(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm'),
        endTimeM: dayjs(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm')
      })
    }

    let punchClockArrary = [];

    if (!newPunchClock.isEmpty) {
      if (isDayOff) {
        let punchInTime = newPunchClock.first;
        let punchOutTime = newPunchClock.last;
        punchClockArrary.push({
          key: punchClockArrary.length,
          punchInTime,
          punchOutTime,
          punchInRevised: punchClockRevises && punchClockRevises.includes(punchInTime),
          punchOutRevised: punchClockRevises && punchClockRevises.includes(punchOutTime)
        });
      } else {
        let punchClockDupe = newPunchClock.all.concat();

        for (let i = 0; i < shifts.length; ++i) {
          let last = i === shifts.length - 1;
          let shift = shifts[i];
          if (shift.ignorePunchClock) continue;

          let shiftStartTime = shift.startTimeS || shift.startTime;
          let shiftEndTime = shift.endTimeS || shift.endTime;
          if (shiftStartTime === '-' && shiftEndTime === '-') {
            punchClockArrary.push({
              key: punchClockArrary.length,
              punchInTime: '-',
              punchOutTime: '-',
              punchInError: false,
              punchOutError: false
            });
          } else {
            const { punchInTime, punchInError } = findNextPunchIn(punchClockDupe, date, shiftStartTime);
            const { punchOutTime, punchOutError, nextPunchClock } = findNextPunchOut(punchClockDupe, date, shiftEndTime, last, false);
            punchClockArrary.push({
              key: punchClockArrary.length,
              punchInTime,
              punchOutTime,
              punchInError,
              punchOutError,
              punchInRevised: punchClockRevises && punchClockRevises.includes(punchInTime),
              punchOutRevised: punchClockRevises && punchClockRevises.includes(punchOutTime)
            });
            if (!last) punchClockDupe = nextPunchClock || [];
          }
        }
      }
    } else {
      if (!isDayOff && shifts.length && !newPunchClock.isExist) {
        punchClockArrary.push({
          key: punchClockArrary.length,
          punchInTime: '-',
          punchOutTime: '-',
          punchInError: true,
          punchOutError: true
        });
      }
    }

    return punchClockArrary
  }

  return (
    <div>
      {punchClockExceptions && punchClockExceptions.map((pcE, idx) => <div className={classes.datePunchClockException} key={idx}>
        <div>
          {pcE.reason}
        </div>
        <div>
          {pcE.startTime}-{pcE.endTime}
        </div>
      </div>)}
      {punchClockArray && punchClockArray.map((pc, idx) => (pc.punchInTime !== '-' || pc.punchOutTime !== '-') && <div className={classes.datePunchClock} key={idx} onClick={() => onEditClick(pc, 'punchClock')}>
        <label className={pc.punchInError ? classes.punchClockError : (pc.punchInRevised ? classes.punchClockRevised : '')}>
          {pc.punchInTime}
        </label>-
        <label className={pc.punchOutError ? classes.punchClockError : (pc.punchOutRevised ? classes.punchClockRevised : '')}>
          {pc.punchOutTime}
        </label>
      </div>)}
      {comments && comments.map((comment, idx) => <div className={classes.dateComment} key={idx} onClick={() => onEditClick(comment, 'editComment')}>
        <label>{comment.comment}</label>
      </div>)}
      {leaves.length > 0 && !isDayOff && leaves.map((leave, idx) => <div className={classes.dateLeave} style={{ backgroundColor: '#fe2851' }} key={idx}>
        <div>
          {formatMessage({ id: `leaveType.${leave.type}` })}
        </div>
        <div>
          {leave.startTime}-{leave.endTime}
        </div>
      </div>)}
      {weeklyLeaves && weeklyLeaves.map((leave, idx) =>
        <div className={classes.dateLeave} style={{ backgroundColor: '#3f51b5' }} key={idx} onClick={() => onEditClick(leave, 'editWeeklyLeave')}>
          <div> {'本休'}</div>
          <div>
            {leave.startTime}-{leave.endTime}
          </div>
        </div>)}
      {
        ['nurse', 'customerService', 'salesRep'].includes(staffType) &&
        process.env.BRANCH_ENV !== 'santea' &&
        date.day() === 0 && punchClockExceptions.length <= 0 && <div className={classes.dateLeave} style={{ backgroundColor: '#3f51b5' }}>
          <div>{'本休'}</div>
          <div>{'All day'}</div>
        </div>
      }
    </div >
  );

}


ScheduleDate.propTypes = {
  currentUser: PropTypes.shape({
    email: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    department: PropTypes.string.isRequired,
    isManagement: PropTypes.bool.isRequired,
    active: PropTypes.bool.isRequired,
  }),
  date: PropTypes.object.isRequired,
  staff: PropTypes.object.isRequired,
  staffId: PropTypes.string.isRequired,
  dataMapping: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  punchClock: PropTypes.object.isRequired,
  punchClockRevise: PropTypes.object.isRequired,
  workTime: PropTypes.object.isRequired,
  isDayOff: PropTypes.bool.isRequired,
  userRight: PropTypes.object.isRequired,
  staffType: PropTypes.string.isRequired,
};

export default ScheduleDate;
